import {RemoteConstantsModel} from "../../models/remoteConstants.model";

// Per-entity variant BRUSH for the Map Editor: which asset-pack images new paint
// strokes / resource placements may pin into the map document. A SET, not one key —
// painting picks randomly among them per cell, so a stroke can lay down a mix of
// variants. An empty/absent entry means "no choice made", and paint falls back to the
// pack default.
//
// The brush lives in localStorage (a workspace preference, not branch or map data);
// the pins it paints ARE map data (tilePaletteImages / resource.image).

const STORAGE_KEY = 'rift.editor.mapImageOverrides';

export type ImageOverrides = { [entityId: string]: string[] };

export function loadImageOverrides(): ImageOverrides {
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '');
        if (typeof raw !== 'object' || raw === null) return {};
        const overrides: ImageOverrides = {};
        for (const [id, value] of Object.entries(raw)) {
            // A bare string is the pre-multi-select format: one brush per entity.
            // Read it as a single-element selection so nobody's stored brush is lost.
            if (typeof value === 'string' && value.length > 0) {
                overrides[id] = [value];
                continue;
            }
            if (!Array.isArray(value)) continue;
            const keys = value.filter((key): key is string => typeof key === 'string' && key.length > 0);
            if (keys.length > 0) overrides[id] = keys;
        }
        return overrides;
    } catch { /* corrupt/absent */ }
    return {};
}

export function saveImageOverrides(overrides: ImageOverrides) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
}

// Every image key an entity's pack can display, deduped, resolvable-only
// (present in state.images). The entity's own id-keyed image (the pack
// top-left convention) is included when it resolves.
export function packImageKeys(state: RemoteConstantsModel, entityId: string): string[] {
    const keys: string[] = [];
    const seen = new Set<string>();
    const add = (key: string | undefined) => {
        if (!key || seen.has(key) || !state.images || !(key in state.images)) return;
        seen.add(key);
        keys.push(key);
    };
    for (const row of state.imagePacks?.[entityId] ?? []) {
        for (const key of row ?? []) add(key);
    }
    add(entityId);
    return keys;
}

// The pack's default image for an entity (top-left — what the game's random
// pick most often shows), else the id-keyed image, else undefined
// (→ placeholder rendering). Used for cells with NO pinned variant.
export function resolveDefaultImageKey(state: RemoteConstantsModel, entityId: string): string | undefined {
    const candidates = packImageKeys(state, entityId);
    return candidates.length > 0 ? candidates[0] : undefined;
}

// The entity's selected pack images, in pack order, filtered to the ones that still
// resolve in this branch. Empty when nothing is selected — or when a stored selection
// refers only to images this branch doesn't have.
export function resolveSelectedImageKeys(
    state: RemoteConstantsModel,
    overrides: ImageOverrides,
    entityId: string,
): string[] {
    const selected = overrides[entityId];
    if (!selected || selected.length === 0) return [];
    const candidates = packImageKeys(state, entityId);
    return candidates.filter(key => selected.includes(key));
}

// One image to pin for a single cell: a uniform pick among the selection, so a stroke
// over many cells scatters the selected variants. Falls back to the pack default when
// nothing is selected, matching the single-brush behaviour this replaced — paint
// always pins something concrete, so a saved map looks the same for everyone.
export function pickImageKey(
    state: RemoteConstantsModel,
    overrides: ImageOverrides,
    entityId: string,
): string | undefined {
    const selected = resolveSelectedImageKeys(state, overrides, entityId);
    if (selected.length === 0) return resolveDefaultImageKey(state, entityId);
    if (selected.length === 1) return selected[0];
    return selected[Math.floor(Math.random() * selected.length)];
}

// Plain click replaces the selection with the one image; ctrl/cmd click toggles that
// image in or out. Toggling the last one off leaves the selection empty, which reads
// the same as never having chosen (see pickImageKey).
export function toggleSelection(selected: string[], key: string, additive: boolean): string[] {
    if (!additive) return [key];
    return selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key];
}
